import React, { useLayoutEffect, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener } from '@mui/material';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { EvaluationModel } from '@pages-clean/risk-assessment/risk-log/constants';
import './styles.scss';
import { useTranslation } from '@hooks/index';

interface EvaluationSelectorProps {
    evaluationModel: EvaluationModel;
    value?: number;
    onChange?: ((value: number) => void) | undefined;
    disabled?: boolean;
    'data-testid': string;
}
const TooltipContentTemplate = (props: { title: string }) => {
    const { title } = props;
    const items = title.split('<br>').map((item: string) => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/no-missing-key
        return <div>{item}</div>;
    });
    return <div style={{ maxHeight: '120px', overflowY: 'auto', maxWidth: '400px', margin: '8px 4px' }}>{items}</div>;
};

const EvaluationSelector = ({
    evaluationModel,
    value,
    onChange,
    disabled,
    'data-testid': dataTestId,
}: EvaluationSelectorProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const columnRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        columnRef.current?.setAttribute('data-testid', dataTestId);
        return () => {
            columnRef.current = null;
        };
    }, [dataTestId]);

    const handleNumberChange = (number: number) => () => {
        !disabled && onChange && onChange(number);
    };

    return (
        <div className="column" ref={columnRef} style={{ flexGrow: evaluationModel.key === 'detectability' ? 0 : 1 }}>
            <div className="button-label">
                <span className="label-row">{evaluationModel.label}&nbsp;*&nbsp;</span>
                <div className="title-tooltip">
                    <ClickAwayListener
                        onClickAway={() => {
                            setShowTooltip(false);
                        }}
                    >
                        <div>
                            <Tooltip
                                parentTitle
                                anchorElement="target"
                                position="top"
                                open={showTooltip}
                                content={(props: { title: string }) => <TooltipContentTemplate title={props.title} />}
                            >
                                <span
                                    role="button"
                                    tabIndex={0}
                                    className="icon-wrap"
                                    onClick={() => {
                                        setShowTooltip(!showTooltip);
                                    }}
                                    onKeyDown={() => {}}
                                    title={evaluationModel.tooltip}
                                >
                                    <FontAwesomeIcon
                                        style={{ transform: 'rotate(180deg)' }}
                                        icon={['fal', 'circle-exclamation']}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </ClickAwayListener>
                </div>
            </div>
            <div className="button-line">
                {evaluationModel.evaluationsOptions
                    .filter((e) => e !== null)
                    .map((elem, index: number, array) => (
                        <div
                            key={`${evaluationModel.key}-${elem?.value}`}
                            className={`button-container ${index !== array.length - 1 && 'mb'}`}
                        >
                            <Tooltip>
                                <button
                                    title={elem?.description ?? undefined}
                                    type="button"
                                    id={`evaluation-${evaluationModel.key}-button-${elem?.value}`}
                                    onClick={handleNumberChange(elem.value)}
                                    className={`round-button ${elem && value === elem.value && 'active'} `}
                                    data-testid={`${evaluationModel.key}-${elem?.value}`}
                                >
                                    <span style={{ padding: '0px' }}>{elem?.value}</span>
                                </button>
                            </Tooltip>
                            <span>{elem?.label}</span>
                        </div>
                    ))}
                {evaluationModel.evaluationsOptions.length > 1 && <div className="line" />}
            </div>
        </div>
    );
};

interface EvaluationSelectorControllerProps<TData, TControl extends FieldValues> {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean | undefined>;
    handleInputChange?: (value: number) => void;
    evaluationModel: EvaluationModel;
    disabled?: boolean;
    required?: boolean;
    'data-testid': string;
}

const EvaluationSelectorController = <TData, TControl extends FieldValues>({
    control,
    name,
    disabled,
    'data-testid': dataTestId,
    required = false,
    validateRules,
    handleInputChange,
    evaluationModel,
}: EvaluationSelectorControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value } }) => {
                return (
                    <EvaluationSelector
                        evaluationModel={evaluationModel}
                        value={value}
                        onChange={(val) => {
                            onChange(val);
                            if (handleInputChange) handleInputChange(val);
                        }}
                        disabled={disabled}
                        data-testid={dataTestId}
                    />
                );
            }}
        />
    );
};
export { EvaluationSelectorController };
export default EvaluationSelector;
